import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import CodeEditor from "../../../components/BuilderComponents/CodeEditor/CodeEditor";
import FieldSelectorFromPopup from "../../../components/BuilderComponents/FieldSelectorFromPopup";
import Button from "../../../components/FormComponents/Button";
import Input from "../../../components/FormComponents/Input";
import { useTableContext } from "../../../context/TableContext";
import useDirtyChecker from "../../../hooks/useDirtyChecker";
import useFetchData from "../../../hooks/useFetchData";
import GenericActionBar from "../../../Layouts/GenericActionBar/GenericActionBar";
import Loader from "../../../Layouts/Loader/Loader";
import axiosInstance, { handleApiError } from "../../../services/axiosService";
import { toastSuccess } from "../../../utils/toastUtils";

const ActivityWebhookApiSection = () => {
  const { apiId } = useParams();
  const { data, loading, error } = useFetchData("/activity-webhook/" + apiId, [apiId]);
  if (loading) {
    return <Loader />;
  }
  if (error) {
    return <div>Error while feching activity webhook</div>;
  }
  return <ActivityWebhookEditor data={data} />;
};
const ActivityWebhookEditor = ({ data }) => {
  const [activityWebhookData, setActivityWebhookData] = useState(data);
  const { markClean, isDirty } = useDirtyChecker(activityWebhookData);
  const [isSaving, setIsSaving] = useState(false);
  const [jsonError, setJsonError] = useState(null);
  const { apiId } = useParams();
  const { tableData } = useTableContext();

  const getSmAndActivityName = useMemo(() => {
    const sm = tableData.find((item) => item.state_machine_id === activityWebhookData.state_machine_id);
    const activity = sm?.table.columns_new.find((item) => item.activity_id === activityWebhookData.activity_id);
    return {
      sm: sm?.table.table_name,
      activity: activity?.name
    };
  }, [activityWebhookData.state_machine_id, activityWebhookData.activity_id, tableData]);
  const { sm, activity } = getSmAndActivityName;

  const handleSaveWebhook = async () => {
    setIsSaving(true);
    try {
      const { name: _name, ...dataToSend } = activityWebhookData;

      const { message } = await axiosInstance.patch(`/activity-webhook/${apiId}`, dataToSend);
      markClean();
      toastSuccess(message);
    } catch (err) {
      handleApiError(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangeReqMapping = (newMapping) => {
    setActivityWebhookData((prev) => ({
      ...prev,
      request: {
        ...prev.request,
        request_mapping: newMapping
      }
    }));
  };

  const handleResponseChange = (code) => {
    try {
      const parsed = JSON.parse(code);
      setActivityWebhookData((prev) => ({ ...prev, response: parsed }));
      setJsonError(null);
    } catch (err) {
      setJsonError(err.message);
    }
  };

  return (
    <div>
      <GenericActionBar
        path={
          <div>
            <em>API</em> → <span>{activityWebhookData.name}</span>
          </div>
        }
        actions={[
          {
            key: "save",
            icon: "save",
            title: isSaving ? "Saving..." : "Save",
            onClick: isSaving ? undefined : handleSaveWebhook
          }
        ]}
        options={{
          save: {
            className: `${isDirty ? "save_glowing" : ""} ${isSaving ? "disabled" : ""}`.trim()
          }
        }}
      />

      <div
        className="p_3 m_auto mt_2 border_full"
        style={{
          maxWidth: "800px",
          background: "#fff",
          borderRadius: "8px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
          padding: "16px",
          marginTop: "16px"
        }}
      >
        {/* State Machine / Activity Info */}
        <div style={{ display: "flex", gap: "24px", marginBottom: "16px", flexWrap: "wrap" }}>
          <div
            style={{
              flex: "1 1 200px",
              background: "#f9f9f9",
              padding: "12px 16px",
              borderRadius: "6px",
              border: "1px solid #e0e0e0"
            }}
          >
            <div className="font_weight_bold" style={{ marginBottom: "4px", color: "#555" }}>
              State Machine
            </div>
            <div style={{ fontSize: "16px", color: "#222" }}>{sm || "—"}</div>
          </div>

          <div
            style={{
              flex: "1 1 200px",
              background: "#f9f9f9",
              padding: "12px 16px",
              borderRadius: "6px",
              border: "1px solid #e0e0e0"
            }}
          >
            <div className="font_weight_bold" style={{ marginBottom: "4px", color: "#555" }}>
              Activity
            </div>
            <div style={{ fontSize: "16px", color: "#222" }}>{activity || "—"}</div>
          </div>
        </div>

        <ActivityWebhookRequestKeyMapping
          mapping={activityWebhookData.request.request_mapping || []}
          onChange={handleChangeReqMapping}
          tableId={activityWebhookData.state_machine_id}
        />

        <h3 style={{ margin: "8px 0" }}>Response</h3>
        <CodeEditor
          height="25vh"
          value={JSON.stringify(activityWebhookData.response, null, 2)}
          language="json"
          handleChange={handleResponseChange}
          enableBacktickTrigger={false}
        />
        {jsonError && <div style={{ color: "red", marginTop: "8px" }}>JSON Error: {jsonError}</div>}
      </div>
    </div>
  );
};

const ActivityWebhookRequestKeyMapping = ({ mapping, onChange, tableId }) => {
  const [tableObj, setTableObj] = useState(null);
  const { getTableTypeData } = useTableContext();

  const handleRemoveMapping = (index) => {
    onChange(mapping.filter((_, i) => i !== index));
  };

  // Update mapping directly from table edits
  const handleMappingChange = (index, key, value) => {
    const updated = [...mapping];
    updated[index][key] = value;
    onChange(updated);
  };

  useEffect(() => {
    const fetchTable = async () => {
      const { tableObject } = await getTableTypeData("statemachine", tableId);
      setTableObj(tableObject);
    };
    fetchTable();
  }, [getTableTypeData, tableId]);

  return (
    <>
      <h3 style={{ marginBottom: "8px" }}>Request Mappings</h3>

      <AddMappingRow tableId={tableId} tableObj={tableObj} onAdd={(newMapping) => onChange([...mapping, newMapping])} />

      <div>
        {mapping.length === 0 ? (
          <p style={{ color: "#666" }}>No mappings added yet.</p>
        ) : (
          <table style={{ tableLayout: "fixed", width: "100%" }}>
            <thead>
              <tr>
                <th style={{ width: "40%" }}>External Field</th>
                <th style={{ width: "40%" }}>Internal Key</th>
                <th style={{ width: "20%" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {mapping.map((mapItem, index) => (
                <tr key={index}>
                  <td>
                    <Input
                      value={mapItem.external_field}
                      onChange={(e) => handleMappingChange(index, "external_field", e.target.value)}
                    />
                  </td>
                  <td>
                    <FieldSelectorFromPopup
                      tableId={tableId}
                      label="Select Internal Field"
                      tableMap={tableObj}
                      tableType={"statemachine"}
                      onSelect={(field) => handleMappingChange(index, "internal_key", field.id)}
                      fieldId={mapItem.internal_key}
                      showDeleteButton={false}
                    />
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <Button variant="delete" onClick={() => handleRemoveMapping(index)}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

const AddMappingRow = ({ tableId, tableObj, onAdd }) => {
  const [externalField, setExternalField] = useState("");
  const [internalKey, setInternalKey] = useState("");

  const handleAdd = () => {
    if (!externalField || !internalKey) return;

    onAdd({
      id: Date.now(), // unique id for table keys
      external_field: externalField,
      internal_key: internalKey
    });

    setExternalField("");
    setInternalKey("");
  };

  const handleInternalKeyChange = (field) => {
    setInternalKey(field.id);
  };

  return (
    <div className="d_flex align_center gap_2 mb_3">
      <Input placeholder="External Field" value={externalField} onChange={(e) => setExternalField(e.target.value)} />
      <FieldSelectorFromPopup
        tableId={tableId}
        tableMap={tableObj}
        tableType="statemachine"
        onSelect={handleInternalKeyChange}
        fieldId={internalKey}
        label="Select Internal Field"
      />
      <Button onClick={handleAdd}>+ Add Mapping</Button>
    </div>
  );
};

export default ActivityWebhookApiSection;
